import { useEffect } from "react";
import { z } from "zod";

import { WarningCircle as WarningCircleIcon } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { AuthApiFactory } from "@/apis/auth";
import { useSubscriptionStore } from "@/store/subscription";
import { useInitializeSession } from "@/hooks/use-initialize";
import { useAuthenticationStore } from "@/store/authentication";
import { wait } from "@/lib/utils";
import { trackLead } from "@/events/analytics";

export const Route = createFileRoute("/auth/_layout/callback/google")({
  validateSearch: z.object({ code: z.string().nullish() }),
  component: RouteComponent,
});

const UnpaidSchema = z.object({
  message: z.string().email(),
  error_code: z.literal("USER_NOT_PAID"),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useSearch();

  const authenticate = useAuthenticationStore((store) => store.authenticate);
  const setSubscription = useSubscriptionStore((store) => store.setSubscription);
  const initializeSession = useInitializeSession();

  const {
    mutate: authenticateWithGoogle,
    status,
    error,
  } = useMutation({
    mutationFn: async (code: string) => {
      const uri = window.location.origin + "/auth/callback/google";
      const [, response] = await Promise.all([wait(2000), AuthApiFactory.Apis.AuthenticateWithGoogle(code, uri)]);
      return response;
    },
    onSuccess: async (response) => {
      const session = await AuthApiFactory.Apis.Authenticate();
      const initialize = await initializeSession();
      authenticate(session.data.user, session.data.organization, initialize.profile);
      setSubscription(initialize.subscription);

      if (response.is_new_user) trackLead(session.data.user.email);
    },
    onError: (error) => {
      const unpaid = UnpaidSchema.safeParse(error);
      if (unpaid.success) {
        navigate({ to: "/auth", search: { plans: true, email: unpaid.data.message } });
      } else {
        setTimeout(() => navigate({ to: "/auth" }), 3000);
      }
    },
  });

  useEffect(() => {
    if (params.code && localStorage.getItem("google_ads_linking")) {
      localStorage.removeItem("google_ads_linking");
      window.location.href = "/oauth/google?code=" + encodeURIComponent(params.code);
      return;
    }

    if (params.code) {
      authenticateWithGoogle(params.code);
    } else {
      setTimeout(() => navigate({ to: "/auth" }), 3000);
    }
  }, [params.code, authenticateWithGoogle, navigate]);

  if (!params.code) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <WarningCircleIcon className="size-8 text-destructive" />
        <p className="text-base font-medium text-foreground mt-3">Failed to login</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-96">We received an invalid code. You will be redirected back in 3 seconds...</p>
      </div>
    );
  }

  switch (status) {
    case "error":
      return (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <WarningCircleIcon className="size-8 text-destructive" />
          <p className="text-base font-medium text-foreground mt-3">Failed to login</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-96">{!error || !error.message ? "Oops! An error occurred" : error.message}. You will be redirected back in 3 seconds...</p>
        </div>
      );

    default:
      return (
        <div className="flex items-center justify-center gap-3 h-full">
          <div className="spinner size-6 !border-foreground" />
          <p className="text-sm text-foreground">Authenticating with Google...</p>
        </div>
      );
  }
}
